import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../../context/UserProvider";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { CameraIcon } from "../util/Icons";
import PlaygroundMenu from "./PlaygroundMenu";

const SYSTEM_PROMPT =
    "Transform the subject or image into an adorable fall-themed plushie with soft felt or fleece textures, rounded proportions, and delicate embroidered details. Preserve the subject's recognizable features while simplifying them into a charming handcrafted stuffed toy. Keep the expression friendly and cozy, with a slightly oversized head, short limbs, and a cuddly silhouette. Use a rich Canadian autumn palette of maple red, burnt orange, golden yellow, warm cream, and deep forest green. Place the plushie against a softly blurred fall landscape with hints of colorful maple leaves, a tree-lined trail, and warm late-afternoon light. Keep the background subtle and unobtrusive, with shallow depth of field and gentle bokeh, so the plushie stays the clear focal point.";
//Summer: "Transform the subject or image into an adorable plushie-style form with soft felt or fleece textures, rounded proportions, and gentle embroidered details. Preserve recognizable traits while simplifying the design into a charming handcrafted stuffed toy. Keep the expression friendly and cute, with a slightly oversized head, short limbs, and a cuddly silhouette. Use a warm Canadian summer color palette inspired by freshwater lakes, evergreen forests, sandy shorelines, and golden sunsets. Soft blues, pine greens, warm creams, and subtle maple-red accents should create a cozy outdoor feel. Place the plushie against a blurred Canadian lakeside landscape. The background should feature gentle hints of a calm lake, distant tree-covered shoreline, rolling hills, and warm summer sky, all rendered with shallow depth of field and soft focus. Keep background details minimal and unobtrusive so the plushie remains the clear focal point, subtle bokeh.";
//Spring: "Transform the subject or image into an adorable plushie-style form with soft textures and rounded proportions. If a person is present, preserve recognizable traits; otherwise, reinterpret the object or animal as a cozy stuffed toy using felt or fleece textures. Give it a cozy felt or fleece texture, simplified shapes, and gentle embroidered details for the eyes, mouth, and features. Use a warm, pastel or neutral color palette with smooth shading and subtle seams, like a handcrafted stuffed toy. Keep the expression friendly and cute, with a slightly oversized head, short limbs, and a cuddly silhouette. The final image should feel like a charming, collectible plush toy — cozy, wholesome, and huggable, while still recognizable as the original subject. With a spring background.";

const MAX_FILE_MB = 4; // keep callable payloads safer
const MAX_GENERATIONS = 5;

export default function ImageGenerator() {
    const { user } = useUserContext();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [generationCount, setGenerationCount] = useState(0);
    const [selectedImage, setSelectedImage] = useState(null);
    const [uploadPreview, setUploadPreview] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [generatedImageSrc, setGeneratedImageSrc] = useState(null);
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

    const loadingMessages = [
        "✨ Warming up the magic...",
        "🎨 Mixing digital paint colors...",
        "🔮 Consulting the creative spirits...",
        "⭐ Sprinkling some imagination dust...",
        "☕ Brewing your masterpiece...",
        "🌈 Adding a dash of creativity...",
        "🎁 Crafting something special...",
        "🖼️ Almost there... patience is art!",
    ];

    const percentUsed = useMemo(() => {
        const p = (generationCount / MAX_GENERATIONS) * 100;
        return Math.max(0, Math.min(100, p));
    }, [generationCount]);

    useEffect(() => {
        if (!user?.uid) return;
        fetchUserStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.uid]);

    // Cleanup object URL
    useEffect(() => {
        return () => {
            if (uploadPreview) URL.revokeObjectURL(uploadPreview);
        };
    }, [uploadPreview]);

    // Cycle through loading messages
    useEffect(() => {
        if (!loading) {
            setLoadingMessageIndex(0);
            return;
        }
        const interval = setInterval(() => {
            setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [loading, loadingMessages.length]);

    async function fetchUserStats() {
        try {
            const userRef = doc(db, "users", user.uid);
            const snapshot = await getDoc(userRef);
            if (snapshot.exists()) {
                const data = snapshot.data();
                setGenerationCount(data.imageGenerationCount || 0);
            } else {
                setGenerationCount(0);
            }
        } catch (err) {
            console.error("Error fetching user stats:", err);
        }
    }

    const processFile = (file) => {
        if (!file) return;

        const allowed = ["image/png", "image/jpeg", "image/webp"];
        if (!allowed.includes(file.type)) {
            setError("Please upload a PNG, JPG, or WEBP image.");
            return;
        }

        const sizeMb = file.size / (1024 * 1024);
        if (sizeMb > MAX_FILE_MB) {
            setError(`Please upload an image smaller than ${MAX_FILE_MB}MB.`);
            return;
        }

        setError("");
        setSelectedImage(file);

        if (uploadPreview) URL.revokeObjectURL(uploadPreview);
        setUploadPreview(URL.createObjectURL(file));

        // reset previous result
        setGeneratedImageSrc(null);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        processFile(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        processFile(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const fileToDataUrl = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (err) => reject(err);
        });

    const handleGenerate = async () => {
        if (!selectedImage) {
            setError("Please upload an image first.");
            return;
        }
        if (generationCount >= MAX_GENERATIONS) {
            setError(`You have reached the limit of ${MAX_GENERATIONS} generations.`);
            return;
        }

        setLoading(true);
        setError("");

        try {
            const imageDataUrl = await fileToDataUrl(selectedImage);
            if (!imageDataUrl || typeof imageDataUrl !== "string" || imageDataUrl.length < 100) {
                setError("Failed to process image. Please try another file.");
                return;
            }

            const functions = getFunctions();
            const generateImage = httpsCallable(functions, "generateImage");

            const rsp = await generateImage({
                prompt: SYSTEM_PROMPT,
                image: imageDataUrl,
            });

            const { b64, outputMimeType } = rsp.data || {};
            if (!b64) {
                throw new Error("No image returned.");
            }

            // Render returned base64 directly
            setGeneratedImageSrc(`data:${outputMimeType || "image/webp"};base64,${b64}`);

            // Server already increments the stored count; we update UI immediately
            setGenerationCount((prev) => prev + 1);
        } catch (err) {
            console.error("Generation failed:", err);

            // Firebase callable errors often look like: "functions/invalid-argument"
            const code = err?.code || "";
            let msg = err?.message || "Failed to generate image. Please try again.";

            if (code.includes("resource-exhausted")) msg = `You have reached the limit of ${MAX_GENERATIONS} generations.`;
            if (code.includes("unauthenticated")) msg = "Please sign in and try again.";
            if (code.includes("invalid-argument")) msg = "Invalid image or prompt. Please try another image.";

            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white pb-4 md:pb-10">
            <PlaygroundMenu />

            <div className="max-w-4xl mx-auto px-0 py-2 md:px-4 md:py-8">
                <div className="flex items-center mb-3 md:mb-6 mt-2">
                    <h1 className="text-xl md:text-3xl font-bold text-slate-800 m-0">Create your plushie avatar</h1>
                </div>

                <div className="bg-white rounded-none md:rounded-2xl shadow-sm border-y md:border border-slate-200 p-4 md:p-10 lg:p-12">
                    <div className="flex flex-col md:flex-row gap-4 md:gap-8">
                        {/* Left Side: Controls */}
                        <div className="flex-1 flex flex-col gap-3 md:gap-6">

                            <div className="flex flex-col gap-2 md:gap-4">
                                {/* Example reference image */}
                                <span className="block text-sm font-medium text-slate-700">Examples of what you can create</span>
                                <div className="flex justify-center">
                                    <img
                                        src="/img/avatar-example-summer.png"
                                        alt="Example plushie avatar"
                                        className="max-h-32 md:max-h-40 rounded-lg shadow-sm"
                                    />
                                </div>

                                <label className="block">
                                    <span className="block text-sm font-medium text-slate-700 mb-2 md:mb-2 mt-4">
                                        Upload your photo and click generate to create your plushie avatar
                                    </span>

                                    <div
                                        className={`
                      border-2 border-dashed rounded-lg md:rounded-xl p-4 md:p-8 text-center cursor-pointer transition-colors
                      ${isDragging
                                                ? "border-blue-500 bg-blue-50"
                                                : selectedImage
                                                    ? "border-green-400 bg-green-50"
                                                    : "border-slate-300 hover:border-slate-400 hover:bg-slate-50"
                                            }
                    `}
                                        onDrop={handleDrop}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                    >
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp"
                                            onChange={handleImageUpload}
                                            className="hidden"
                                            id="image-upload"
                                        />

                                        <label htmlFor="image-upload" className="cursor-pointer w-full h-full block">
                                            {uploadPreview ? (
                                                <div className="relative">
                                                    <img
                                                        src={uploadPreview}
                                                        alt="Preview"
                                                        className="max-h-48 mx-auto rounded-lg shadow-sm"
                                                    />
                                                    <p className="mt-2 text-sm text-green-600 font-medium">Click to change</p>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center gap-2 text-slate-500">
                                                    <CameraIcon size={32} />
                                                    <p className="m-0 font-medium">Click or drag & drop to upload</p>
                                                    <p className="m-0 text-xs">PNG / JPG / WEBP up to {MAX_FILE_MB}MB</p>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                </label>
                            </div>

                            {error && (
                                <div className="bg-red-50 text-red-600 p-2 md:p-3 rounded-lg text-sm border border-red-100">
                                    {error}
                                </div>
                            )}

                            <button
                                onClick={handleGenerate}
                                disabled={loading || generationCount >= MAX_GENERATIONS || !selectedImage}
                                className={`
                  py-2.5 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl font-bold text-white shadow-md transition-all text-sm md:text-base
                  ${loading || generationCount >= MAX_GENERATIONS || !selectedImage
                                        ? "bg-slate-300 cursor-not-allowed"
                                        : "bg-gradient-to-r from-blue-500 to-indigo-600 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                                    }
                `}
                            >
                                {loading ? "Generating..." : "Generate My Plushie"}
                            </button>

                            {/* Compact generations counter */}
                            <div className="flex items-center gap-2 text-xs text-blue-600">
                                <span>
                                    {generationCount}/{MAX_GENERATIONS} generations used
                                </span>
                                <div className="flex-1 bg-blue-200 rounded-full h-1.5">
                                    <div
                                        className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                                        style={{ width: `${percentUsed}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Side: Result */}
                        <div className="flex-1 flex flex-col">
                            <span className="block text-sm font-medium text-slate-700 mb-1 md:mb-2">Result</span>

                            <div className="flex-1 min-h-[250px] md:min-h-[400px] bg-slate-100 rounded-lg md:rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden relative">
                                {loading && (
                                    <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-10">
                                        <div className="flex flex-col items-center gap-3 md:gap-4 p-4 md:p-6 max-w-xs text-center">
                                            {/* Animated spinner */}
                                            <div className="relative">
                                                <div className="w-16 h-16 border-4 border-blue-200 rounded-full" />
                                                <div className="absolute inset-0 w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                            </div>

                                            {/* Rotating fun message */}
                                            <p className="text-blue-600 font-semibold text-lg m-0 transition-all duration-300">
                                                {loadingMessages[loadingMessageIndex]}
                                            </p>

                                            {/* Time estimate */}
                                            <p className="text-slate-500 text-sm m-0">
                                                This usually takes 30-60 seconds
                                            </p>

                                            {/* Warning */}
                                            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                                                <span className="text-amber-500 text-lg">⚠️</span>
                                                <p className="text-amber-700 text-xs m-0 font-medium">
                                                    Please don't close or refresh this page
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {generatedImageSrc ? (
                                    <div className="w-full h-full relative group flex flex-col">
                                        <img
                                            src={generatedImageSrc}
                                            alt="Generated"
                                            className="w-full h-full object-contain flex-1"
                                        />

                                        <div className="flex flex-col gap-2 p-2 md:p-3 bg-slate-50/80 border-t border-slate-200">
                                            {/* Privacy notice */}
                                            <p className="text-slate-500 text-xs m-0 text-center p-2 mt-2">
                                                Please download or post your picture. We do not store your images.
                                            </p>
                                            <div className="flex gap-2 justify-center">
                                                <a
                                                    href={generatedImageSrc}
                                                    download="generated-image.webp"
                                                    className="text-slate-700 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-slate-200 transition-colors border border-slate-300 bg-white"
                                                >
                                                    Download
                                                </a>
                                                <button
                                                    onClick={() => {
                                                        // Convert data URL to File and navigate to social feed
                                                        fetch(generatedImageSrc)
                                                            .then(res => res.blob())
                                                            .then(blob => {
                                                                const file = new File([blob], "ai-generated-image.webp", { type: blob.type });
                                                                navigate("/", { state: { createdImage: file } });
                                                            });
                                                    }}
                                                    className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
                                                >
                                                    Post to Feed
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-slate-400 text-center p-4 md:p-8">
                                        <p className="m-0 text-sm md:text-base">Your generated image will appear here.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* end right */}
                    </div>
                </div>
            </div>
        </div>
    );
}
